import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { finalize, Subscription } from 'rxjs';
import { BaseComponent } from 'src/app/moov-common/base/base.component';
import { ChatMessage, ChatMessageRatingEnum, ChatMessageRoles, ChatMetadata, ChatSpecialString, WebSocketMessageData } from 'src/app/moov-common/services/chat/chat-data';
import { AbstractChatService } from 'src/app/moov-common/services/chat/chat.abstract.service';
import { ChatMessageDataType, ChatTypeEnum } from 'src/app/moov-common/services/chat/chatEnum';
import { StructuredProcessInstanceStatusEnum } from 'src/app/moov-common/services/chat/strcutredProcess-data';
import { ClaroEvents } from 'src/app/moov-common/services/google-analytics/analytics.enum';
import { UtilsService } from 'src/app/moov-common/services/utils/utils.service';
import { ChatUserPromptComponent } from '../chat-user-prompt/chat-user-prompt.component';

// messages are loaded page by page: the newest page on open, older pages when the user scrolls up
const MESSAGES_PAGE_SIZE = 30;
const LOAD_OLDER_MESSAGES_SCROLL_THRESHOLD_PX = 100;

@Component({
  selector: 'app-structured-process-chat',
  templateUrl: './structured-process-chat.component.html',
  styleUrl: './structured-process-chat.component.scss'
})
export class StructuredProcessChatComponent extends BaseComponent implements OnInit {

  @Input() processId: string = '';
  @Input() stageId: string = '';
  @Input() stepId: string = '';
  @Input() currentStepStatus: StructuredProcessInstanceStatusEnum = StructuredProcessInstanceStatusEnum.PENDING;
  @Output() newMessageReceived = new EventEmitter<ChatMessage>();
  @Output() startNextStepEvent = new EventEmitter<void>();

  @ViewChild('chatMessagesContainer') private chatMessagesContainer : ElementRef;
  @ViewChild('userPrompt') userPrompt: ChatUserPromptComponent;

  activeChat: ChatMetadata ;
  chatMessages: ChatMessage[] = [];
  private socketSubscription: Subscription;
  liveMessage: string = '';
  isLoading: boolean = false;
  presentLiveMessage: boolean = false;
  isEmpty: boolean = true;
  hasOlderMessages: boolean = false;
  isLoadingOlderMessages: boolean = false;
  chatMessageRoles = ChatMessageRoles;
  structuredProcessInstanceStatusEnumInstance = StructuredProcessInstanceStatusEnum;

  constructor(
    private readonly chatService: AbstractChatService  ) {
      super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.openChatOrCreateOne();

    // console.log(`StructuredProcessChatComponent ngOnInit stepId is ${this.stepId} currentStepStatus is ${this.currentStepStatus}`);

  }

  ngOnChanges(changes: SimpleChanges): void {
    this.ifChanged(changes['stepId'], ()=>this.stepChanged());
  }

  stepChanged(): void {
    this.openChatOrCreateOne();
  }

  openChatOrCreateOne(){
    // console.log(`StructuredProcessChatComponent ngOnInit stepId is ${this.stepId} currentStepStatus is ${this.currentStepStatus}`);
    // end existing chat
    this.endChat();
    this.isLoading = true;
    this.chatMessages = [];
    this.isEmpty = true;
    this.hasOlderMessages = false;
    this.isLoadingOlderMessages = false;
    this.presentLiveMessage = false;
    this.liveMessage = '';
    // console.log(`openChatOrCreateOne processId is ${this.processId} stageId is ${this.stageId} stepId is ${this.stepId}`);
    this.chatService.getStructuredProcessChat(this.processId, this.stageId, this.stepId).subscribe((chat) => {
      if (chat != null) {
        // console.log(`openChatOrCreateOne got existing chat is ${JSON.stringify(chat)}`);
        this.activeChat = chat;
        this.chatService.liveChat_connect(chat.id);
        this.startChatSequence();
        this.isLoading = false;
      } else {
        // console.log(`openChatOrCreateOne no existing chat for this process, stage and step, create a new one with id ${this.processId} ${this.stageId} ${this.stepId}`);
        this.chatService.startNewStructuredProcessChat(ChatTypeEnum.StructuredProcess, this.processId, this.stageId, this.stepId).subscribe((newChat) => {        
          this.activeChat = newChat;
          this.chatService.liveChat_connect(newChat.id);
          this.startChatSequence();
          this.isLoading = false;
        });
      }
    });
  }

  startChatSequence(isNewChat = false): void {
    // console.log(`startChatSequence active chat is ${this.activeChatId}`);

    this.chatService.liveChat_connect(this.activeChat.id);
    this.subscribeToLiveStream();
    // there are chat types, that even for new ones it still contains messages
    this.getChatLatestMessages();
  }

  subscribeToLiveStream(): void {
    this.socketSubscription = this.chatService.onMessage().subscribe((msg) => {
      // console.log(`subscribeToLiveStream msg is ${JSON.stringify(msg)}`);
      let message: WebSocketMessageData;
      try {
        message = JSON.parse(msg);
        // console.log(`subscribeToLiveStream message is ${JSON.stringify(message)}`);
      } catch (error) {
        console.error('Error parsing message:', error);
        return;
      }

      if (message.dataType === 'text') {
        this.handleTextMessageDataFromServer(message.data);
      }
      else if (message.dataType === 'audio') {
        // do nothing
        
      }
    });
  }

  handleTextMessageDataFromServer(msg: string): void {
    if (!msg.includes(ChatSpecialString.TERMINATION_STRING)) {
      this.liveMessage += msg;
      // console.log(`handleTextMessageDataFromServer liveMessage is msg ${this.liveMessage}`);
      setTimeout(()=> this.scrollToBottom(), 0)
    }
    else{
      // message Completed
      let msgId = this.utilsService.chat_extractUUID(msg);
      if (!msgId) {
        msgId = '';
      }

      // console.log(`subscribeToLiveStream message completed msgId is ${msgId} msg was ${msg}`);

      let formattedText = this.utilsService.chat_formatRawMessage(this.liveMessage);

      const serverMessage: ChatMessage = {
        id: msgId,
        userId: this.user!.id,
        chatId: this.activeChat.id,
        content: formattedText,
        role: ChatMessageRoles.SERVER,
        creationTimestamp: new Date(),
        userRating: ChatMessageRatingEnum.NATURAL,
      };

      this.chatMessages.push(serverMessage);
      // console.log(`handleTextMessageDataFromServer oldMessages is ${JSON.stringify(this.oldMessages)}`);
      this.liveMessage = '';
      this.presentLiveMessage = false;
      this.analytics.sendClaroEvent(ClaroEvents.Conversational_structuredProcessChatMessage);
      this.getChatLatestMessages();
      // console.log(`handleTextMessageDataFromServer calling newMessageReceived.emit msgId is ${msgId}`);
      this.analytics.sendClaroEvent(ClaroEvents.StructuredProcess_AssistantMessageReceived, { processId: this.processId, stepId: this.stepId });
      this.newMessageReceived.emit(serverMessage);
    }
  }

  getChatLatestMessages(): void {
    const chatId = this.activeChat.id;
    this.chatService.getOldMessages(chatId, MESSAGES_PAGE_SIZE).subscribe((messages) => {
      if (chatId !== this.activeChat?.id) {
        // the step was changed meanwhile
        return;
      }

      if (messages.length > 0) {
        const latestMessages = this.formatServerMessages(messages);

        // the latest page replaces the newest messages (local ones get their server ids), older pages loaded by scrolling up are kept
        const firstLatestMessageIdx = this.chatMessages.findIndex((msg) => msg.id === latestMessages[0].id);
        const isFirstLoad = firstLatestMessageIdx < 0;
        const olderMessages = isFirstLoad ? [] : this.chatMessages.slice(0, firstLatestMessageIdx);
        this.chatMessages = [...olderMessages, ...latestMessages];

        if (isFirstLoad) {
          this.hasOlderMessages = messages.length === MESSAGES_PAGE_SIZE;
        }

        this.isEmpty = false;

        // instant on the first load - a smooth scroll from the top would trigger loading older messages
        setTimeout(()=>this.scrollToBottom(isFirstLoad), 0);

      } else {
        this.chatMessages = [];
        this.isEmpty = true;
        this.hasOlderMessages = false;
      }
    });
  }

  onMessagesScroll(): void {
    const container = this.chatMessagesContainer?.nativeElement;
    if (container && container.scrollTop <= LOAD_OLDER_MESSAGES_SCROLL_THRESHOLD_PX) {
      this.loadOlderMessages();
    }
  }

  loadOlderMessages(): void {
    const oldestMessageId = this.chatMessages[0]?.id;
    if (!this.hasOlderMessages || this.isLoadingOlderMessages || !oldestMessageId) {
      return;
    }

    const chatId = this.activeChat.id;
    this.isLoadingOlderMessages = true;
    this.chatService.getOldMessages(chatId, MESSAGES_PAGE_SIZE, oldestMessageId)
      .pipe(finalize(() => {
        if (chatId === this.activeChat?.id) {
          this.isLoadingOlderMessages = false;
        }
      }))
      .subscribe((messages) => {
        if (chatId !== this.activeChat?.id) {
          return;
        }

        const container = this.chatMessagesContainer?.nativeElement;
        const distanceFromBottom = container ? container.scrollHeight - container.scrollTop : 0;

        const loadedIds = new Set(this.chatMessages.map((msg) => msg.id));
        const olderMessages = this.formatServerMessages(messages).filter((msg) => !loadedIds.has(msg.id));
        this.chatMessages = [...olderMessages, ...this.chatMessages];
        this.hasOlderMessages = messages.length === MESSAGES_PAGE_SIZE;

        // keep the messages the user is reading in place after the older ones are added above them
        setTimeout(() => {
          if (container) {
            container.scrollTo({ top: container.scrollHeight - distanceFromBottom, behavior: 'instant' });
          }
        }, 0);
      });
  }

  private formatServerMessages(messages: ChatMessage[]): ChatMessage[] {
    for (let currMessage of messages) {
      currMessage.content = this.utilsService.chat_formatRawMessage(currMessage.content);
      currMessage.content = this.utilsService.removeStructuredProcessMarkupsFromText(currMessage.content);
    }

    return messages.sort((a, b) =>
      a.creationTimestamp > b.creationTimestamp ? 1 : b.creationTimestamp > a.creationTimestamp ? -1 : 0);
  }

  submitUserEntry(userEntry: string): void{
    if (this.isEmpty) {
      this.isEmpty = false;
    }
    
    this.sendUserMessage(userEntry);
    this.userPrompt.focusOnInput();
    setTimeout(()=> this.scrollToBottom(), 0)
  }

  sendUserMessage(userText: string): void {
    const userChatMessage = this.createUserMessage(userText);

    this.chatMessages.push(userChatMessage);

    if (this.isEmpty) {
      this.isEmpty = false;
    }

    this.liveMessage = '';
    this.presentLiveMessage = true;

    const messageData: WebSocketMessageData = {dataType: ChatMessageDataType.Text, data: userText};
    this.chatService.liveChat_sendMessage(JSON.stringify(messageData));
    this.analytics.sendClaroEvent(ClaroEvents.Conversational_userMessage);
    this.analytics.sendClaroEvent(ClaroEvents.Conversational_userTextMessage);
    this.analytics.sendClaroEvent(ClaroEvents.StructuredProcess_UserMessageSent, { processId: this.processId, stepId: this.stepId });
  }
  
  createUserMessage(userText: string): ChatMessage {
    const userMessage: ChatMessage = {
      id: '',
      userId: this.user!.id,
      chatId: this.activeChat.id,
      content: userText,
      role: ChatMessageRoles.USER,
      creationTimestamp: new Date(),
      userRating: ChatMessageRatingEnum.NATURAL,
    };

    return userMessage;
  }

  scrollToBottom(instant: boolean = false): void{
    if (this.chatMessagesContainer){
      try {
        const container = this.chatMessagesContainer.nativeElement;
        container.scrollTo({ top: container.scrollHeight, behavior: instant ? 'instant' : 'smooth' });
      } catch (error) {
        console.log(error);
      }
    }
  }

  startNextStep(): void {
    this.startNextStepEvent.emit();
  }

  restartStep(stepId: string): void {
    this.isLoading = true;
    this.chatService.deleteChat(this.activeChat.id).subscribe(() => {
      this.isLoading = false;
      this.openChatOrCreateOne();
    });
  }

  messageLiked(msgId: string): void {
    this.chatService
      .setMessageRating(msgId, ChatMessageRatingEnum.LIKED)
      .subscribe();
    this.changeMessageRating(msgId, ChatMessageRatingEnum.LIKED);
    this.analytics.sendClaroEvent(ClaroEvents.Conversational_messageLiked);
  }

  messageDisliked(msgId: string): void {
    this.chatService
      .setMessageRating(msgId, ChatMessageRatingEnum.DISLIKED)
      .subscribe();
    this.changeMessageRating(msgId, ChatMessageRatingEnum.DISLIKED);
    this.analytics.sendClaroEvent(ClaroEvents.Conversational_messageDisliked);
  }

  changeMessageRating(
    messageId: string,
    userRating: ChatMessageRatingEnum
  ): void {
    let messageToChange = this.chatMessages.find((msg) => msg.id === messageId);

    if (messageToChange) {
      messageToChange.userRating = userRating;
    }
  }

  ngOnDestroy() {
    this.endChat();
  }

  endChat(): void {
    // console.log(`endChat`);
    if (this.socketSubscription) this.socketSubscription.unsubscribe();
    this.chatService.liveChat_closeConnection();
  }

  forceUserTextToChat(userText: string): void {
    this.submitUserEntry(userText);
  }
}
